import { useMemo } from 'react';

import type { JobCluster } from '~/shared/data-contract/job-cluster';

import { useChangesetState } from '../onboarding/OnboardingChangesetProvider';
import { useFamiliesWithClusters } from './use-families-with-clusters';

export type UseJobClusterDetailsReturn = {
    readonly data: JobCluster | undefined;
    readonly error: Error | null;
    readonly isError: boolean;
    readonly isLoading: boolean;
};

/** Resolves cluster by ID from changeset first, then backend. */
export const useJobClusterDetails = (
    architectureId: string,
    familyId: string,
    clusterId: string,
): UseJobClusterDetailsReturn => {
    const { addedRows } = useChangesetState();
    const fromChangeset = useMemo(() => {
        const family = addedRows.find((f) => f.id === familyId);
        return family?.subRows?.find((c) => c.id === clusterId)?.data;
    }, [addedRows, familyId, clusterId]);
    const {
        data: familiesWithClusters,
        error,
        isError,
        isLoading,
    } = useFamiliesWithClusters(architectureId, {
        enabled: fromChangeset === undefined,
    });

    const fromServer = useMemo(() => {
        const family = familiesWithClusters?.find((f) => f.id === familyId);
        return family?.clusters.find((c) => c.id === clusterId);
    }, [familiesWithClusters, familyId, clusterId]);

    const data = fromChangeset ?? fromServer;

    if (fromChangeset !== undefined) {
        return { data: fromChangeset, error: null, isError: false, isLoading: false };
    }

    return {
        data,
        error: error ?? null,
        isError,
        isLoading,
    };
};
